import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'

import { Input } from '@src/elements/input/input'
import { bulletColorMapping } from '@src/util/constants'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { FormInputRow } from '@src/routes/members/components/body-measurements/form-input-row'
import { Caption } from '@src/elements/typography/typography'
import { Bullet } from '@src/elements/bullet/bullet'
import EventBus from '@src/lib/event_bus'
import { snakecaseKeys } from '@src/util/functions'

import './form.sass'
import './body-measurements-goals.sass'
import { convertLengthForDisplaying, convertToCM, LengthUnit, WeightUnit } from '@src/util/weight'

@Component({
  computed: {
    weightUnit () {
      return this.$store.state.club.weightUnit.toUpperCase()
    },
    lengthUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class BodyMeasurementGoals extends Vue {

  /**
   * The properties in goals objects need to correspond to the translations
   */
  @Prop({ required: true })
  private formData: any

  @Prop({ required: false })
  private goals: any

  @Prop({ required: true })
  private translationKey: string

  @Prop({ required: true })
  private type: 'weight-goals' | 'circumferences-goals'

  private weightUnit: string

  private apiClient: ApiClient

  private initialGoals: any[] = []
  private newGoal: any = {}
  private lengthUnit: LengthUnit

  constructor () {
    super()
    this.apiClient = new ApiClient('')
  }

  created () {
    EventBus.on('applyMeasurementsChange', this.save)

    this.newGoal = Object.assign({}, this.formData)
    this.initialGoals = Object.assign([], this.goals)
  }

  beforeDestroy () {
    EventBus.off('applyMeasurementsChange', this.save)
  }

  save (type) {
    if (this.type === type) {
      Object.keys(this.newGoal).forEach((key) => {
        if (this.newGoal[key] && (this.newGoal[key] > 0 || key === 'body_vfat')) {
          const goal = {
            metric: key,
            value: (['waistline', 'right_bicep_circumference', 'left_bicep_circumference', 'right_thigh_circumference', 'left_thigh_circumference', 'hip_measurement', 'chest_circumference'].indexOf(key) >= 0) ?
            convertToCM(this.newGoal[key], this.lengthUnit) : this.newGoal[key],
            effectiveAt: new Date().toISOString()
          }
          this.apiClient.post(API.TRAINERS_API.BODY_MEASUREMENT_GOALS.replace('{memberId}', this.$route.params.id), { body_measurement_goal: snakecaseKeys(goal) })
            .then(() => {
              // display the previously set goal and empty form without reloading the data
              if (!this.initialGoals[key]) {
                this.initialGoals[key] = []
              }
              this.initialGoals[key].push(goal)
              this.newGoal = Object.assign({}, this.formData)
              return this.$store.dispatch('showToast', { message: this.$t('BODY_MEASUREMENTS.SAVE_SUCCESS'), fadeOut: true })
            })
            .catch(error => console.log('cannot save body measurement goal', error))
        } else {
          this.newGoal[key] = null
        }
      })
    }
  }

  render (h: CreateElement) {
    const goals = Object.keys(this.newGoal).map(key => {
      const placeholder = ['body_fat', 'muscle_mass', 'body_water_mass'].includes(key) ? '%' : key === 'body_vfat' ? '' : key === 'body_weight' ? this.$t(`UNITS.${this.weightUnit}`) : this.$t(`UNITS.${this.lengthUnit.toUpperCase()}`)
      let currentGoal = (typeof this.initialGoals[key] !== 'undefined' && this.initialGoals[key].length > 0) ?
        this.initialGoals[key].slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)[0].value : null
      if (['waistline', 'right_bicep_circumference', 'left_bicep_circumference', 'right_thigh_circumference', 'left_thigh_circumference', 'hip_measurement', 'chest_circumference'].indexOf(key) >= 0) {
        currentGoal = convertLengthForDisplaying(currentGoal, this.lengthUnit, 2)
      }
      return <FormInputRow>
        <div>
          <Input type='number' ref='goalInput' value={this.newGoal[key]}
                 htmlLabel={<div><Bullet color={bulletColorMapping[key]} />{`${this.$t(this.translationKey + key.toUpperCase())}${placeholder ? '/' : ''}${placeholder}`}</div>}
                 valueChanged={(value) => this.newGoal[key] = value} />
          <Caption class='current-goal'>
            {currentGoal !== null ? `${this.$t('PAGE.MEMBERS.STATUS.CURRENT_GOAL')} ${currentGoal} ${placeholder}` : this.$t('PAGE.MEMBERS.STATUS.CURRENT_GOAL_NOT_PROVIDED')}
          </Caption>
        </div>
      </FormInputRow>
    })

    return <div class='body-measurement-goals'>
      {goals}
    </div>
  }
}
